// Shopify remains the authority for bundle allocations, totals and shipping progress.
class CustomCartItems extends HTMLElement {
  connectedCallback() {
    this.customAbort = new AbortController();
    this.customRoot = this.closest('cart-drawer');
    const customOptions = { signal: this.customAbort.signal };
    this.addEventListener('click', this.customClick.bind(this), customOptions);
    this.addEventListener('change', this.customChange.bind(this), customOptions);
    this.customRoot.addEventListener('submit', customEvent => {
      if (this.customBusy) customEvent.preventDefault();
    }, { ...customOptions, capture: true });

  }

  disconnectedCallback() {
    this.customAbort?.abort();

  }

  customClick(customEvent) {
    const customRemove = customEvent.target.closest('[data-custom-cart-remove]');
    const customStep = customEvent.target.closest('[data-custom-cart-step]');
    if (!customRemove && !customStep) return;
    customEvent.preventDefault();
    if (this.customBusy) return;
    const customCard = (customRemove || customStep).closest('[data-custom-cart-key]');
    const customInput = customCard.querySelector('[data-custom-cart-quantity]');
    if (customRemove) {
      this.customUpdate(customCard, 0);
    } else {
      Number(customStep.dataset.customCartStep) < 0 ? customInput.stepDown() : customInput.stepUp();
      this.customCommit(customInput);
    }
  }

  customChange(customEvent) {
    if (customEvent.target.matches('[data-custom-cart-quantity]')) this.customCommit(customEvent.target);
  }

  customCommit(customInput) {
    if (this.customBusy) return;
    if (!customInput.value || !customInput.checkValidity()) {
      customInput.reportValidity();
      customInput.value = customInput.defaultValue;
      return;
    }
    const customQuantity = Number(customInput.value);
    if (customQuantity !== Number(customInput.defaultValue)) {
      this.customUpdate(customInput.closest('[data-custom-cart-key]'), customQuantity);
    }
  }

  async customUpdate(customCard, customQuantity) {
    if (this.customBusy) return;
    // A customer edit takes priority over the background checkout-return refresh.
    // Cancel it before changing controls so stale markup cannot interrupt the edit.
    this.customRoot.customRefreshController?.abort();
    this.customBusy = true;
    this.customRoot.setAttribute('aria-busy', 'true');
    customCard.setAttribute('aria-busy', 'true');
    const customError = customCard.querySelector('[data-custom-cart-error]');
    customError.hidden = true;
    const customControls = [...this.customRoot.querySelectorAll('[data-custom-cart-step]:not(:disabled), input[data-custom-cart-quantity]:not(:disabled), [name="checkout"]:not(:disabled)')];
    customControls.forEach(customControl => { customControl.disabled = true; });
    try {
      const customSections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: customSections } }));
      const customSectionId = this.customRoot.closest('.shopify-section').id.replace('shopify-section-', '');
      if (!customSections.includes(customSectionId)) customSections.push(customSectionId);
      const customResponse = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: customCard.dataset.customCartKey, quantity: customQuantity, sections: customSections, sections_url: window.location.pathname })
      });
      const customResult = await customResponse.json();
      if (!customResponse.ok) throw new Error(customResult.description || this.dataset.customError);
      // The native drawer receives Shopify-rendered cards, totals and shipping in one update.
      if (!customResult.sections?.[customSectionId]) {
        const customSectionResponse = await fetch(`${window.Shopify.routes.root}?section_id=${encodeURIComponent(customSectionId)}`);
        if (!customSectionResponse.ok) throw new Error(this.dataset.customError);
        customResult.sections = { ...customResult.sections, [customSectionId]: await customSectionResponse.text() };
      }
      if (!customResult.sections[customSectionId].includes('id="cart-drawer"')) throw new Error(this.dataset.customError);
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'custom-cart:change', cart: customResult, customLineKey: customCard.dataset.customCartKey }
      }));
      this.customRoot.removeAttribute('aria-busy');
    } catch (customFailure) {
      customCard.querySelector('[data-custom-cart-quantity]').value = customCard.querySelector('[data-custom-cart-quantity]').defaultValue;
      customError.textContent = customFailure instanceof TypeError || customFailure instanceof SyntaxError ? this.dataset.customError : customFailure.message;
      customError.hidden = false;
      customControls.forEach(customControl => { customControl.disabled = false; });
      customCard.removeAttribute('aria-busy');
      this.customRoot.removeAttribute('aria-busy');
      this.customBusy = false;
    }
  }
}

if (!customElements.get('custom-cart-items')) customElements.define('custom-cart-items', CustomCartItems);
